import { useMemo } from 'react'

import { ActionIcon, Box, Divider, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'
import type { Step, StepsResult } from 'types/graphql'

import Button from 'src/components/Buttons/Button/Button'
import ExpirationIndicator from 'src/components/DataDisplay/ExpirationIndicator/ExpirationIndicator'
import StepStatusMenu from 'src/components/Overlays/StepStatusMenu/StepStatusMenu'
import { DocumentStatus } from 'src/graphql/types/documents'
import { StepType, StepStatus } from 'src/graphql/types/steps'
import IconAdd from 'src/icons/IconAdd'
import IconEmptyInbox from 'src/icons/IconEmptyInbox'
import IconRefresh from 'src/icons/IconRefresh'
import IconRightArrow from 'src/icons/IconRightArrow'
import IconUploadSimple from 'src/icons/IconUploadSimple'
import { isStepTypeTracked } from 'src/lib/expirationTracking'
import { formatStepType, getStepLabel } from 'src/lib/formatters'
import { sortStepsByTemplate } from 'src/lib/stepOrder'
import StepFindingsBadge from 'src/pages/ApplicantDetailsPage/sidebar/components/StepItem/StepFindingsBadge'

import SyncStepsModal from '../SyncStepsModal/SyncStepsModal'

import DQFDocument from './components/DQFDocument'
import RequestOrderModal from './components/RequestOrderModal'

interface DQFContentProps {
  steps: StepsResult
  documentsResponse: any
  onEdit: (document: Document, step: Step) => void
  onPreview: (document: any) => void
  onDelete: (documentId: string, step: Step) => void
  onRenewed: (documentId: string, isRenewed: boolean) => void
  onDownload: (document: any) => void
  handleUpload: (selected: any) => void
  onStepChange: (step: Step) => void
  refetchQueries: any
  onError: (error: any) => void
  applicantId: string
  applicantInfo?: any
  selected: Step
  setSelected: (selected: Step) => void
  onOpenTracker?: (trackerId: string) => void
  isUploadInProgress?: boolean
}

const DQFContent = ({
  steps,
  documentsResponse,
  onEdit,
  onPreview,
  onDelete,
  onRenewed,
  onDownload,
  onStepChange,
  handleUpload,
  refetchQueries,
  onError,
  applicantId,
  applicantInfo,
  selected,
  setSelected,
  onOpenTracker,
  isUploadInProgress,
}: DQFContentProps) => {
  const [
    requestOrderModalOpened,
    { open: openRequestOrderModal, close: closeRequestOrderModal },
  ] = useDisclosure(false)
  const [
    syncStepsModalOpened,
    { open: openSyncStepsModal, close: closeSyncStepsModal },
  ] = useDisclosure(false)

  const DOT_TEXT = `It is suggested that the ${formatStepType(
    StepType.DOT_APP
  )} be completed prior to requesting.`
  const isDotComplete = useMemo(() => {
    return !!steps.items.find(
      (step) =>
        step.type === StepType.DOT_APP && step.status === StepStatus.COMPLETED
    )
  }, [steps])

  const orderedSteps = useMemo(
    () => sortStepsByTemplate(steps.items, applicantInfo?.jobListing),
    [steps.items, applicantInfo?.jobListing]
  )

  const handleStepSelection = (step) => {
    setSelected(step)
    onStepChange(step)
  }

  const [expiredDocuments, currentDocuments, renewedDocuments] = useMemo(() => {
    const expired = []
    const current = []
    const renewed = []

    documentsResponse?.forEach((doc) => {
      if (doc.isRenewed) {
        renewed.push(doc)
      } else if (
        !doc.expirationDate || // Treat documents without an expiration date as current
        dayjs().isBefore(dayjs(doc.expirationDate).endOf('day'))
      ) {
        current.push(doc)
      } else {
        expired.push(doc)
      }
    })

    return [expired, current, renewed]
  }, [documentsResponse])

  return (
    <div className=" grid h-[90%] grid-cols-[3fr_4fr] gap-4 ">
      <div className="flex flex-col gap-3 overflow-auto ">
        <Box className="font-lg flex flex-row items-center justify-between font-bold text-doubleNickel-gray-700">
          <div className="flex flex-row items-center gap-1">
            Steps
            <Tooltip label="Sync Steps" withArrow position="top">
              <ActionIcon
                variant="subtle"
                size="sm"
                color="gray"
                onClick={openSyncStepsModal}
                aria-label="Sync Steps"
              >
                <IconRefresh className="mt-0.5 h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-400" />
              </ActionIcon>
            </Tooltip>
          </div>
          <Tooltip
            label={DOT_TEXT}
            withArrow
            disabled={isDotComplete}
            className="text-xs"
          >
            <Button
              text="Request"
              lefticon={<IconAdd />}
              onClick={openRequestOrderModal}
              // disabled={!isDotComplete}
            />
          </Tooltip>
        </Box>

        <div className="flex flex-col gap-8">
          <div className="flex flex-col">
            {orderedSteps.map((step) => {
              const activeDocuments = step.documents.filter(
                (doc) => doc.status != DocumentStatus.ARCHIVED && !doc.isRenewed
              )
              const currentDocument = activeDocuments[0]
              const isTracked = isStepTypeTracked(step.type as StepType)

              const hasMissingDate =
                isTracked && !!currentDocument && !currentDocument.expirationDate

              const nearestDate = currentDocument?.expirationDate ?? null

              const isNearestExpired =
                nearestDate &&
                dayjs(nearestDate)
                  .startOf('day')
                  .isBefore(dayjs().startOf('day'))

              const showExpiration = isNearestExpired
                ? nearestDate
                : hasMissingDate
                ? null
                : nearestDate

              return (
                <Box
                  key={step.stepId}
                  onClick={() => handleStepSelection(step)}
                  className={`flex cursor-pointer flex-row items-center justify-between border-b px-2 py-3 text-xs hover:text-doubleNickel-brand-500
                    ${
                      (selected.customStepId ?? selected.type) ===
                      (step.customStepId ?? step.type)
                        ? 'rounded-lg bg-doubleNickel-brand-25 font-semibold text-doubleNickel-brand-500'
                        : 'font-medium text-doubleNickel-gray-600'
                    }`}
                >
                  <span className="flex flex-row items-center">
                    {getStepLabel(step)}
                    <StepFindingsBadge step={step} applicantId={applicantId} />
                  </span>
                  <div className="flex flex-row items-center gap-2">
                    {(showExpiration || hasMissingDate) && (
                      <ExpirationIndicator
                        expirationDate={showExpiration}
                        isTrackedCategory={hasMissingDate}
                        textClassName="text-doubleNickel-gray-500"
                        missingLabel=""
                      />
                    )}
                    <StepStatusMenu
                      stepId={step.stepId}
                      status={step.status}
                      size="s"
                      refetchQueries={refetchQueries}
                      onError={onError}
                    />
                  </div>
                </Box>
              )
            })}
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-row gap-4 overflow-hidden">
        <Divider orientation="vertical" />
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden">
          <Box className="font-lg flex flex-row items-center justify-between font-bold text-doubleNickel-gray-700">
            <div className="flex flex-row items-center gap-2">
              {selected.type && getStepLabel(selected)}
              <IconRightArrow className="h-3 fill-none stroke-doubleNickel-gray-900" />
              <div>Documents</div>
            </div>

            <Button
              text="Upload"
              variant="outline"
              lefticon={<IconUploadSimple />}
              onClick={() => handleUpload(selected)}
              disabled={isUploadInProgress}
            />
          </Box>
          {((documentsResponse && documentsResponse.length === 0) ||
            !selected.type) && (
            <div className="flex flex-1 flex-col items-center justify-center">
              <IconEmptyInbox className="fill-none" />
              <div className="text-sm font-medium text-doubleNickel-gray-600">
                {selected.type
                  ? 'There are no documents for this step'
                  : 'Please select a step to see the corresponding files'}
              </div>
            </div>
          )}

          {currentDocuments?.length > 0 && (
            <div>
              <div className="text-sm font-semibold text-doubleNickel-gray-600 ">
                Current
              </div>
              {currentDocuments.map((document) => {
                return (
                  <DQFDocument
                    key={document.documentId}
                    step={selected}
                    document={document}
                    onDelete={onDelete}
                    onRenewed={onRenewed}
                    onPreview={onPreview}
                    onDownload={onDownload}
                    onEdit={onEdit}
                    onOpenTracker={onOpenTracker}
                    applicantInfo={applicantInfo}
                    refetchQueries={refetchQueries}
                  />
                )
              })}
            </div>
          )}

          {expiredDocuments?.length > 0 && (
            <div>
              <div className="text-sm font-semibold text-doubleNickel-gray-600 ">
                Expired
              </div>
              {expiredDocuments.map((document) => {
                return (
                  <DQFDocument
                    key={document.documentId}
                    step={selected}
                    document={document}
                    onDelete={onDelete}
                    onRenewed={onRenewed}
                    onPreview={onPreview}
                    onDownload={onDownload}
                    onEdit={onEdit}
                    onOpenTracker={onOpenTracker}
                    applicantInfo={applicantInfo}
                    refetchQueries={refetchQueries}
                  />
                )
              })}
            </div>
          )}

          {renewedDocuments?.length > 0 && (
            <div>
              <div className="text-sm font-semibold text-doubleNickel-gray-600 ">
                Renewed
              </div>
              {renewedDocuments.map((document) => {
                return (
                  <DQFDocument
                    key={document.documentId}
                    step={selected}
                    document={document}
                    onDelete={onDelete}
                    onRenewed={onRenewed}
                    onPreview={onPreview}
                    onDownload={onDownload}
                    onEdit={onEdit}
                    onOpenTracker={onOpenTracker}
                    applicantInfo={applicantInfo}
                    refetchQueries={refetchQueries}
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>
      <RequestOrderModal
        refetchQueries={refetchQueries}
        isModalOpen={requestOrderModalOpened}
        onModalClose={closeRequestOrderModal}
        applicantId={applicantId}
      />
      <SyncStepsModal
        applicantId={applicantId}
        isOpen={syncStepsModalOpened}
        onClose={closeSyncStepsModal}
        refetchQueries={refetchQueries}
      />
    </div>
  )
}

export default DQFContent
